import { MutationCache, QueryCache } from '@tanstack/react-query'
import { ApiError } from '@/lib/api-error'

/**
 * Type guard for ApiError instances thrown by API-facing mutations.
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/**
 * Extracts a user-facing error message from an error.
 *
 * - 4xx ApiError → returns the API's specific message (e.g. "Seat limit exceeded")
 * - 5xx ApiError → generic server error (API internals hidden from users)
 * - Non-ApiError  → returns the provided fallback
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (!isApiError(error)) return fallback
  if (error.status >= 500)
    return 'Server error occurred. Please try again later.'
  return error.message || fallback
}

interface ErrorHandlerOptions {
  showUserNotification?: (message: string, type: 'error' | 'warning') => void
  onAuthenticationError?: () => void
}

export function createQueryErrorHandler(options: ErrorHandlerOptions = {}) {
  const { showUserNotification, onAuthenticationError } = options

  const handleError = (
    error: unknown,
    _context: string,
    meta?: Record<string, unknown>,
  ) => {
    // Allow components to suppress the global toast when they handle errors locally
    if (meta?.suppressGlobalError) return

    // Handle ApiError instances from API-facing flows
    if (isApiError(error)) {
      // 401 — delegate to auth handler, no toast
      if (error.status === 401) {
        onAuthenticationError?.()
        return
      }

      // 404 — silent
      if (error.status === 404) {
        return
      }

      // 5xx — generic server error
      if (error.status >= 500) {
        showUserNotification?.(
          'Server error occurred. Please try again later.',
          'error',
        )
        return
      }

      // 4xx (400, 403, 409, etc.) — show the API's specific message
      showUserNotification?.(
        error.message || 'An error occurred. Please try again.',
        'error',
      )
      return
    }

    // Handle network errors
    if (error instanceof Error) {
      if (error.name === 'NetworkError' || error.message.includes('fetch')) {
        showUserNotification?.(
          'Network error. Please check your connection.',
          'error',
        )
        return
      }
    }

    // Generic error fallback
    showUserNotification?.(
      'An unexpected error occurred. Please try again.',
      'error',
    )
  }

  return {
    queryCache: new QueryCache({
      onError: (error, query) => {
        handleError(error, `Query [${query.queryKey.join(', ')}]`, query.meta)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        handleError(
          error,
          `Mutation [${mutation.options.mutationKey?.join(', ') || 'Unknown'}]`,
          mutation.options.meta,
        )
      },
    }),
  }
}

/**
 * Global error reporting function for manual error handling
 */
export const reportError = (
  error: unknown,
  context: string,
  additionalData?: Record<string, unknown>,
) => {
  const errorData = {
    error:
      error instanceof Error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : error,
    context,
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    ...additionalData,
  }

  void errorData // Error data available for Sentry or future reporting
}

/**
 * Retry function for failed operations with exponential backoff
 */
export const createRetryFunction = (
  maxRetries = 3,
  baseDelay = 1000,
  maxDelay = 10000,
) => {
  return (failureCount: number, error: unknown) => {
    // Don't retry on client errors (4xx) except 429 (rate limit)
    if (isApiError(error)) {
      if (error.status >= 400 && error.status < 500 && error.status !== 429) {
        return false
      }
    }

    if (failureCount >= maxRetries) {
      return false
    }

    // Exponential backoff with jitter
    const delay = Math.min(
      baseDelay * Math.pow(2, failureCount) + Math.random() * 1000,
      maxDelay,
    )

    return delay
  }
}
